import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBillingStatus } from "../context/BillingStatusContext";
import { ExpenseOptionsProvider } from "../context/ExpenseOptionsContext";

const EXPENSE_TABS = [
  { label: "Dashboard", path: "/expense" },
  { label: "Upload",    path: "/expense/upload" },
  { label: "Expenses",  path: "/expense/receipts" },
  { label: "Reports",   path: "/expense/reports" },
  { label: "Insights",  path: "/expense/insights" },
  { label: "Budget",    path: "/expense/budget" },
  { label: "Search",    path: "/expense/search" },
];

const AGENT_META = {
  "/bizcard":    { label: "Business Card Agent",    accent: "#2563eb", icon: "📇" },
  "/compliance": { label: "Compliance Bidding Agent", accent: "#7c3aed", icon: "📋" },
  "/expense":    { label: "Personal Expense Manager", accent: "#059669", icon: "💰" },
  "/bni":        { label: "BNI Chapter Advisor",      accent: "#0f766e", icon: "🤝" },
};

const BASE_STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg:       #f0f4fc;
    --surface:  #ffffff;
    --border:   #c0d0eb;
    --border-2: #d5e3f5;
    --text:     #0d1a36;
    --text-2:   #2a4a78;
    --muted:    #6080aa;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    color-scheme: light;
  }
  body { background: var(--bg); color: var(--text); }

  .layout-root { min-height: 100vh; display: flex; flex-direction: column; }

  .layout-topbar {
    position: sticky; top: 0; z-index: 100;
    height: 52px;
    display: flex; align-items: center; gap: 0;
    background: rgba(240,244,252,.92);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border-2);
    flex-shrink: 0;
  }

  .back-btn {
    width: 52px; height: 52px;
    display: flex; align-items: center; justify-content: center;
    background: none; border: none; border-right: 1px solid var(--border-2);
    color: var(--text-2); cursor: pointer; font-size: 16px;
    transition: color .15s, background .15s;
    flex-shrink: 0;
  }
  .back-btn:hover { color: var(--text); background: var(--surface); }

  .layout-brand {
    display: flex; align-items: center; gap: 10px;
    padding: 0 20px; flex: 1;
    min-width: 0;
  }
  .layout-agent-icon {
    font-size: 16px; flex-shrink: 0;
  }
  .layout-title {
    font-size: 13px; font-weight: 600;
    letter-spacing: -0.01em; color: var(--text);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .layout-breadcrumb {
    font-size: 12px; color: var(--muted);
    display: flex; align-items: center; gap: 6px;
  }
  .breadcrumb-sep { opacity: 0.4; }

  .layout-right {
    display: flex; align-items: center; gap: 12px; padding: 0 16px;
    flex-shrink: 0;
  }
  .layout-user {
    font-size: 12px; color: var(--muted);
    white-space: nowrap;
    display: none;
  }
  @media (min-width: 640px) { .layout-user { display: block; } }

  .layout-logout {
    background: none; border: 1px solid var(--border);
    border-radius: 5px; padding: 4px 10px;
    font-size: 11px; font-weight: 500; color: var(--muted);
    cursor: pointer; transition: border-color .15s, color .15s;
    letter-spacing: 0.02em;
  }
  .layout-logout:hover { border-color: #dc2626; color: #dc2626; }

  .accent-bar { height: 2px; width: 100%; flex-shrink: 0; }

  /* ── Expense tab strip ── */
  .tab-strip {
    display: flex; align-items: stretch;
    background: var(--surface);
    border-bottom: 1px solid var(--border-2);
    padding: 0 20px;
    overflow-x: auto;
    scrollbar-width: none;
    flex-shrink: 0;
  }
  .tab-strip::-webkit-scrollbar { display: none; }

  .tab-item {
    display: flex; align-items: center;
    padding: 0 14px; height: 40px;
    font-size: 12px; font-weight: 500;
    color: var(--muted); white-space: nowrap;
    cursor: pointer; border: none; background: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color .15s, border-color .15s;
    letter-spacing: 0.01em;
  }
  .tab-item:hover { color: var(--text-2); }
  .tab-item.active { color: var(--tab-accent); border-bottom-color: var(--tab-accent); font-weight: 600; }

  /* ── Page content ── */
  .layout-content { flex: 1; padding: 28px 32px; max-width: 1200px; width: 100%; margin: 0 auto; }
  @media (max-width: 640px) { .layout-content { padding: 20px 16px; } }

  /* ── Low-credit banner ── */
  .billing-banner {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 20px; font-size: 12px; font-weight: 500;
    flex-shrink: 0;
  }
  .billing-banner.low       { background: rgba(245,158,11,.1);  color: #b45309; }
  .billing-banner.critical  { background: rgba(248,113,113,.1); color: #dc2626; }
  .billing-banner.exhausted { background: rgba(248,113,113,.18); color: #dc2626; font-weight: 700; }
`;

function LowCreditBanner() {
  const billing = useBillingStatus();
  if (!billing || billing.status === "ok") return null;
  const messages = {
    low:       `Low AI credits — ${billing.usagePct}% used. Consider requesting a top-up.`,
    critical:  `Critical: ${billing.usagePct}% of AI credits used — top up soon to avoid interruption.`,
    exhausted: "AI credits exhausted — AI agents will not run until your organisation tops up.",
  };
  return <div className={`billing-banner ${billing.status}`}>&#9888; {messages[billing.status]}</div>;
}

function TopBar({ icon, title, accent, user, logout, navigate }) {
  const initials = (user?.name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <>
      <div className="layout-topbar">
        <button className="back-btn" onClick={() => navigate("/")} title="Back to home">
          ←
        </button>
        <div className="layout-brand">
          <span className="layout-agent-icon">{icon}</span>
          <span className="layout-title">{title}</span>
        </div>
        <div className="layout-right">
          <span className="layout-user">{user?.name}</span>
          <button className="layout-logout" onClick={logout}>Sign out</button>
        </div>
      </div>
      <LowCreditBanner />
    </>
  );
}

export function AgentLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const meta = Object.entries(AGENT_META).find(([k]) => pathname.startsWith(k))?.[1]
    || { label: "Agent", accent: "#2563eb", icon: "🤖" };

  return (
    <>
      <style>{BASE_STYLES}</style>
      <div className="layout-root">
        <TopBar icon={meta.icon} title={meta.label} accent={meta.accent}
                user={user} logout={logout} navigate={navigate} />
        <div className="accent-bar" style={{ background: meta.accent }} />
        <div className="layout-content">{children}</div>
      </div>
    </>
  );
}

export function ExpenseLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const meta = AGENT_META["/expense"];

  return (
    <>
      <style>{BASE_STYLES}</style>
      <div className="layout-root">
        <TopBar icon={meta.icon} title={meta.label} accent={meta.accent}
                user={user} logout={logout} navigate={navigate} />
        <div className="accent-bar" style={{ background: meta.accent }} />
        <div className="tab-strip" style={{ "--tab-accent": meta.accent }}>
          {EXPENSE_TABS.map(t => (
            <button key={t.path}
                    className={`tab-item${pathname === t.path ? " active" : ""}`}
                    onClick={() => navigate(t.path)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="layout-content">
          <ExpenseOptionsProvider>{children}</ExpenseOptionsProvider>
        </div>
      </div>
    </>
  );
}
